/// A prefix that uniquely identifies this plugin, prepended onto the name of any variable generated by this plugin.
const variablePrefix = "diplomat";

/** @import { DataDrivenPropertyValueSpecification as PropertyValue, ExpressionSpecification as Expression, LayerSpecification, Var } from 'maplibre-gl' */

/**
 * Returns a list of languages as a comma-delimited string from the given URL hash.
 * @param {URL | Location} url
 */
export function getLanguageFromURL(url) {
  let language = new URLSearchParams(url.hash.substr(1)).get("language");
  return language === "" ? null : language;
}

/**
 Returns the powerset of the given array.
 */
function powerset(array) {
  return array.reduce(
    (accum, value) => [...accum, ...accum.map((pick) => [...pick, value])],
    [[]],
  );
}

/**
 Compatibility shim for `Intl.Locale.prototype.variants`.
 */
function getVariants(locale) {
  return locale.variants ?? locale.minimize().baseName.match(/-([-\w]+)/)?.[1];
}

/**
 Returns an array of the language tags related to the given language tag, sorted from most specific to least specific.
 
 @param {string} tag - The language tag that the returned language tags are related to.
 @returns {string[]} A sorted array of related language tags, or an empty array if `tag` is malformed.
 */
export function getRelatedLanguageTags(tag) {
  let locale;
  try {
    locale = new Intl.Locale(tag);
  } catch {
    return [];
  }

  let maximized = locale.maximize();
  // The subtags and extensions used in OSM name:*=* subkeys.
  let quals = [
    maximized.script,
    maximized.region,
    getVariants(maximized),
    maximized.numberingSystem,
  ].filter((q) => q);
  // Get all the combinations of components and convert them to language tags.
  let tags = powerset(quals).map((quals) =>
    [maximized.language, ...quals].join("-"),
  );

  // Add the original language tag, in case maximizing it dropped miscellaneous extensions.
  tags.push(locale + "");
  tags.push(locale.baseName);

  // Validate each of the language tags.
  let locales = tags
    .map((tag) => {
      try {
        return new Intl.Locale(tag);
      } catch {}
    })
    .filter((l) => l);

  // Sort the locales from most specific to least specific.
  let scoreLocale = (locale) => {
    let tag = locale + "";
    let score = 0;
    score += tag.length ?? 0;
    score += tag.split("-").length ?? 0;
    // Prioritize variants, which are more specific than script and region.
    score += getVariants(locale)?.length ?? 0;
    // Prioritize extensions, which Intl.Locale.prototype.maximize can’t maximize.
    score += tag.match(/(?:-[tx]-|-u-\w\w-)\w+/)?.[0]?.length ?? 0;
    return score;
  };
  locales.sort((a, b) => scoreLocale(b) - scoreLocale(a));

  let validTags = locales.map((l) => l + "");
  return [...new Set(validTags)];
}

/**
 * Returns the languages that the user prefers.
 */
export function getLocales() {
  // Check the language "parameter" in the hash.
  let parameter = getLanguageFromURL(window.location)?.split(",");
  // Fall back to the user's language preference.
  let userLocales = parameter ?? navigator.languages ?? [navigator.language];
  // Get a full fallback list.
  let tags = userLocales.flatMap((l) => getRelatedLanguageTags(l));

  return [...new Set(tags)];
}

const defaultUnlocalizedNameProperty = "name";

/**
 * Returns a `coalesce` expression that resolves to the feature's name in a
 * language that the user prefers.
 *
 * @param {string[]} locales - Locales of the name fields to include.
 * @param {object} options
 * @param {boolean} [options.includesLegacyFields] - Whether to include the older fields
 *  that include underscores, for layers that have not transitioned to the
 *  colon syntax.
 * @param {string} [options.unlocalizedNameProperty] - The name of the property holding the unlocalized name.
 * @param {string} [options.localizedNamePropertyFormat] - The format of properties holding localized names, where `$1` is replaced by an IETF language tag.
 * @returns {Expression}
 */
export function getLocalizedNameExpression(locales, options = {}) {
  let nameFields = [
    ...locales.flatMap((l) => {
      let localizedNamePropertyFormat =
        options.localizedNamePropertyFormat || `name:$1`;
      let localizedNameProperty = localizedNamePropertyFormat.replaceAll(
        "$1",
        l,
      );
      let fields = [localizedNameProperty];
      // transportation_label uses an underscore instead of a colon.
      // https://github.com/openmaptiles/openmaptiles/issues/769
      if (options.includesLegacyFields && (l === "de" || l === "en")) {
        fields.push(localizedNameProperty.replaceAll(":", "_"));
      }
      return fields;
    }),
    options.unlocalizedNameProperty || defaultUnlocalizedNameProperty,
  ];
  return ["coalesce", ...nameFields.map((f) => ["get", f])];
}

/**
 * Mutates a `let` expression to have a new value for the variable by the given name, binding the variable if it isn’t already bound to any value.
 *
 * @param {PropertyValue<string> | undefined} letExpr - Expression to update.
 * @param {string} variable - Name of the variable to set.
 * @param {*} value - The variable's new value.
 */
export function updateVariable(letExpr, variable, value) {
  if (!letExpr || letExpr[0] !== "let") return;

  let variableNameIndex = letExpr.indexOf(variable);
  if (variableNameIndex % 2 === 1) {
    letExpr[variableNameIndex + 1] = value;
  } else {
    letExpr.splice(-1, 0, variable, value);
  }
}

const localizedNameVariable = `${variablePrefix}__localizedName`;
const localizedCollatorVariable = `${variablePrefix}__localizedCollator`;
const diacriticInsensitiveCollatorVariable = `${variablePrefix}__diacriticInsensitiveCollator`;

/**
 * Recursively walks an expression, returning a copy of the subexpression after replacing any reference to a specific feature property with a new value in place.
 *
 * @param {Expression} expression - The expression to transform.
 * @param {string} propertyName - The name of the feature property to look for.
 * @param {*} replacement - The replacement value.
 * @returns {Expression | undefined} The same array as `expression` if `expression` referred to `propertyName`, or `undefined` if `expression` did not refer to `propertyName`.
 */
export function replacePropertyReferences(
  expression,
  propertyName,
  replacement,
) {
  if (!Array.isArray(expression) || expression[0] === "literal") return;
  if (expression[0] === "get") {
    if (expression.length === 2 && expression[1] === propertyName) {
      return replacement;
    }
    return;
  }
  let didReplace = false;
  expression.forEach((arg, idx) => {
    if (!idx) return; // operator can never be a property reference
    let newValue = replacePropertyReferences(arg, propertyName, replacement);
    if (newValue !== undefined) {
      expression[idx] = newValue;
      didReplace = true;
    }
  });
  if (didReplace) return expression;
}

/**
 * The separator to use in inline contexts.
 */
const inlineSeparator = " \u2022 ";

/**
 * Transforms a layer’s `text-field` layout property so that it can be localized by `localizeLayers` later on.
 *
 * The transformed `text-field` property is only modified if it contains a reference to the feature property specified by `unlocalizedNameProperty`. If the layer’s `symbol-placement` layout property is set to either `line` or `line-center`, the resulting text field takes up only one line. Otherwise, the text field for a given feature may span multiple lines if its unlocalized name property is set to a list of values.
 *
 * @param {LayerSpecification} layer - The style layer to prepare for localization.
 * @param {string} [unlocalizedNameProperty] - The name of the feature property that holds the unlocalized name. References to this property are replaced by a more complex expression that can be localized dynamically.
 * @param {boolean} [glossLocalNames] - Whether to format each label as a dual language label including a local name gloss.
 */
export function prepareLayer(layer, unlocalizedNameProperty, glossLocalNames) {
  let textField = layer.layout && layer.layout["text-field"];
  if (
    !textField ||
    (textField[0] === "let" && textField.includes(localizedNameVariable))
  ) {
    return;
  }

  let symbolPlacement = layer.layout && layer.layout["symbol-placement"];
  let isInline =
    symbolPlacement === "line" || symbolPlacement === "line-center";
  let listValues = glossLocalNames
    ? localizedNameWithLocalGlossCore
    : isInline
      ? localizedNameInlineCore
      : localizedNameCore;
  let newTextField = replacePropertyReferences(
    textField,
    unlocalizedNameProperty || defaultUnlocalizedNameProperty,
    listValues,
  );
  if (newTextField !== undefined) {
    layer.layout["text-field"] = [
      "let",
      localizedNameVariable,
      "",
      newTextField,
    ];
  }
}

/**
 * Updates localizable variables at the top level of the layer's `text-field` expression based on the given locales.
 *
 * @param {LayerSpecification} layer - The style layer to localize.
 * @param {string} collationLocale - The locale for string comparison purposes.
 * @param {Expression} localizedNameExpression - An expression that produces a localized name.
 * @param {Expression} legacyLocalizedNameExpression - An expression that produces a localized name based on legacy properties in OpenMapTiles.
 */
function localizeLayer(
  layer,
  collationLocale,
  localizedNameExpression,
  legacyLocalizedNameExpression,
) {
  if (!("layout" in layer) || !("text-field" in layer.layout)) return;

  let textField = layer.layout["text-field"];

  updateVariable(
    textField,
    localizedNameVariable,
    // https://github.com/openmaptiles/openmaptiles/issues/769
    layer["source-layer"] === "transportation_name"
      ? legacyLocalizedNameExpression
      : localizedNameExpression,
  );

  updateVariable(textField, localizedCollatorVariable, [
    "collator",
    {
      "case-sensitive": false,
      "diacritic-sensitive": true,
      locale: collationLocale,
    },
  ]);

  // Only perform diacritic folding in English. English normally uses few diacritics except when labeling foreign place names on maps.
  updateVariable(textField, diacriticInsensitiveCollatorVariable, [
    "collator",
    {
      "case-sensitive": false,
      "diacritic-sensitive": !/^en\b/.test(collationLocale),
      locale: collationLocale,
    },
  ]);
}

/**
 * Updates localizable variables at the top level of each layer's `text-field` expression based on the given locales.
 *
 * @param {LayerSpecification[]} layers - The style layers to localize.
 * @param {string[]} locales - The locales to insert into each layer.
 * @param {object} options
 * @param {string} [options.unlocalizedNameProperty] - The name of the property holding the unlocalized name.
 * @param {string} [options.localizedNamePropertyFormat] - The format of properties holding localized names, where `$1` is replaced by an IETF language tag.
 */
export function localizeLayers(layers, locales = getLocales(), options = {}) {
  let localizedNameExpression = getLocalizedNameExpression(locales, options);
  let legacyLocalizedNameExpression = getLocalizedNameExpression(locales, {
    ...options,
    includesLegacyFields: true,
  });
  for (let layer of layers) {
    localizeLayer(
      layer,
      locales[0],
      localizedNameExpression,
      legacyLocalizedNameExpression,
    );
  }
}

/**
 * Returns an expression interpreting the given string as a list of tag values,
 * pretty-printing the standard semicolon delimiter with the given separator.
 *
 * https://wiki.openstreetmap.org/wiki/Semi-colon_value_separator
 *
 * The returned expression can be complex, so use it only once within a property
 * value. To reuse the evaluated value, bind it to a variable in a let
 * expression.
 *
 * @param {Expression} valueList A semicolon-delimited list of values.
 * @param {string | Expression} separator A string to insert between each value, or an expression that
 *  evaluates to this string.
 * @param {string | Expression} [valueToOmit]
 * @returns {Expression}
 */
export function listValuesExpression(valueList, separator, valueToOmit) {
  // Replace the ;; escape sequence with a placeholder sequence unlikely to
  // legitimately occur inside a value or separator.
  const objReplacementChar = "\x91\ufffc\x92"; // https://overpass-turbo.eu/s/1pJx
  let escapedValueList = [
    "join",
    ["split", valueList, ";;"],
    objReplacementChar,
  ];

  // Collapse any space following the delimiter.
  let collapsedValueList = ["join", ["split", escapedValueList, "; "], ";"];

  let valuesVariable = `${variablePrefix}__values`;
  let omissionIndexVariable = `${variablePrefix}__omissionIndex`;
  let abridgedValuesVariable = `${variablePrefix}__abridgedValues`;
  return [
    "let",
    valuesVariable,
    ["split", collapsedValueList, ";"],
    [
      "let",
      omissionIndexVariable,
      valueToOmit ? ["index-of", valueToOmit, ["var", valuesVariable]] : -1,
      [
        "let",
        abridgedValuesVariable,
        [
          "match",
          ["var", omissionIndexVariable],
          -1,
          // Nothing to elide.
          ["join", ["var", valuesVariable], separator],
          // Produce the items before and after the omission index, eliding the value at that index.
          [
            "concat",
            [
              "join",
              [
                "slice",
                ["var", valuesVariable],
                0,
                ["var", omissionIndexVariable],
              ],
              separator,
            ],
            // Leave only a separator in place of the omitted item, unless it’s at either end of the list.
            [
              "case",
              [
                "any",
                ["==", ["var", omissionIndexVariable], 0],
                [
                  "==",
                  ["var", omissionIndexVariable],
                  ["-", ["length", ["var", valuesVariable]], 1],
                ],
              ],
              "",
              separator,
            ],
            [
              "join",
              [
                "slice",
                ["var", valuesVariable],
                ["+", ["var", omissionIndexVariable], 1],
              ],
              separator,
            ],
          ],
        ],
        // Unescape any escaped semicolons.
        [
          "join",
          ["split", ["var", abridgedValuesVariable], objReplacementChar],
          ";",
        ],
      ],
    ],
  ];
}

const localizedNameCore = listValuesExpression(
  ["var", localizedNameVariable],
  "\n",
);

/**
 * The names in the user's preferred language, each on a separate line.
 * @type {Expression}
 */
export const localizedName = [
  "let",
  localizedNameVariable,
  "",
  localizedNameCore,
];

const localizedNameInlineCore = listValuesExpression(
  ["var", localizedNameVariable],
  inlineSeparator,
);

/**
 * The names in the user's preferred language, all on the same line.
 * @type {Expression}
 */
export const localizedNameInline = [
  "let",
  localizedNameVariable,
  "",
  localizedNameInlineCore,
];

/**
 * Returns an expression that tests whether the target has the given prefix,
 * respecting word boundaries.
 */
function startsWithExpression(target, candidatePrefix, collator) {
  // "Quebec City" vs. "Québec", "Washington, D.C." vs. "Washington"
  let wordBoundaries = " ,";
  return [
    "all",
    [
      "==",
      ["slice", target, 0, ["length", candidatePrefix]],
      candidatePrefix,
      collator,
    ],
    [
      "in",
      [
        "slice",
        // Pad the target in case the prefix matches exactly.
        // "Montreal " vs. "Montréal"
        ["concat", target, wordBoundaries[0]],
        ["length", candidatePrefix],
        ["+", ["length", candidatePrefix], 1],
      ],
      wordBoundaries,
    ],
  ];
}

function overwritePrefixExpression(target, newPrefix) {
  return ["concat", newPrefix, ["slice", target, ["length", newPrefix]]];
}

/**
 * Returns an expression that tests whether the target has the given suffix,
 * respecting word boundaries.
 */
function endsWithExpression(target, candidateSuffix, collator) {
  let wordBoundary = " ";
  let suffixStartVariable = `${variablePrefix}__suffixStart`;
  return [
    "let",
    suffixStartVariable,
    ["-", ["length", target], ["length", candidateSuffix]],
    [
      "all",
      [
        "==",
        ["slice", target, ["var", suffixStartVariable]],
        candidateSuffix,
        collator,
      ],
      [
        "==",
        [
          "slice",
          target,
          ["-", ["var", suffixStartVariable], 1],
          ["var", suffixStartVariable],
        ],
        wordBoundary,
      ],
    ],
  ];
}

function overwriteSuffixExpression(target, newSuffix) {
  return [
    "concat",
    ["slice", target, 0, ["-", ["length", target], ["length", newSuffix]]],
    newSuffix,
  ];
}

const localizedNameListVariable = `${variablePrefix}__localizedNameList`;
const nameListVariable = `${variablePrefix}__nameList`;

const localizedNameWithLocalGlossCore = [
  "let",
  localizedNameListVariable,
  listValuesExpression(["var", localizedNameVariable], "\n"),
  [
    "case",
    // If the name in the preferred and local languages match exactly...
    [
      "==",
      ["var", localizedNameVariable],
      ["get", "name"],
      ["var", localizedCollatorVariable],
    ],
    // ...just pick one.
    ["format", ["var", localizedNameListVariable]],
    [
      "let",
      nameListVariable,
      listValuesExpression(["get", "name"], "\n"),
      [
        "case",
        // If the name in the preferred language is the same as the name in the
        // local language except for the omission of diacritics and/or the addition
        // of a suffix (e.g., "City" in English)...
        startsWithExpression(
          ["var", localizedNameVariable],
          ["get", "name"],
          ["var", diacriticInsensitiveCollatorVariable],
        ),
        // ...then replace the common prefix with the local name.
        [
          "format",
          overwritePrefixExpression(
            ["var", localizedNameVariable],
            ["var", nameListVariable],
          ),
        ],
        // If the name in the preferred language is the same as the name in the
        // local language except for the omission of diacritics and/or the addition
        // of a prefix (e.g., "City of" in English or "Ciudad de" in Spanish)...
        endsWithExpression(
          ["var", localizedNameVariable],
          ["get", "name"],
          ["var", diacriticInsensitiveCollatorVariable],
        ),
        // ...then replace the common suffix with the local name.
        [
          "format",
          overwriteSuffixExpression(
            ["var", localizedNameVariable],
            ["var", nameListVariable],
          ),
        ],
        // Otherwise, gloss the name in the local language if it differs from the
        // localized name.
        [
          "format",
          ["var", localizedNameListVariable],
          "\n",
          "(\u2068",
          { "font-scale": 0.8 },
          listValuesExpression(["get", "name"], inlineSeparator, [
            "var",
            localizedNameVariable,
          ]),
          { "font-scale": 0.8 },
          "\u2069)",
          { "font-scale": 0.8 },
        ],
      ],
    ],
  ],
];

/**
 * The name in the user's preferred language, followed by the name in the local
 * language in parentheses if it differs.
 * @type {Expression}
 */
export const localizedNameWithLocalGloss = [
  "let",
  localizedNameVariable,
  "",
  localizedCollatorVariable,
  ["collator", {}],
  diacriticInsensitiveCollatorVariable,
  ["collator", {}],
  localizedNameWithLocalGlossCore,
];

/**
 * ISO 3166-1 alpha-2 country codes by ISO 3166-1 alpha-3 code.
 *
 * Source: https://www.cia.gov/the-world-factbook/references/country-data-codes/
 */
const iso3166_1_alpha_2_by_3 = {
  ABW: "AW",
  AFG: "AF",
  AGO: "AO",
  AIA: "AI",
  ALB: "AL",
  AND: "AD",
  ARE: "AE",
  ARG: "AR",
  ARM: "AM",
  ASM: "AS",
  ATA: "AQ",
  ATF: "TF",
  ATG: "AG",
  AUS: "AU",
  AUT: "AT",
  AZE: "AZ",
  BDI: "BI",
  BEL: "BE",
  BEN: "BJ",
  BFA: "BF",
  BGD: "BD",
  BGR: "BG",
  BHR: "BH",
  BHS: "BS",
  BIH: "BA",
  BLM: "BL",
  BLR: "BY",
  BLZ: "BZ",
  BMU: "BM",
  BOL: "BO",
  BRA: "BR",
  BRB: "BB",
  BRN: "BN",
  BTN: "BT",
  BVT: "BV",
  BWA: "BW",
  CAF: "CF",
  CAN: "CA",
  CCK: "CC",
  CHE: "CH",
  CHL: "CL",
  CHN: "CN",
  CIV: "CI",
  CMR: "CM",
  COD: "CD",
  COG: "CG",
  COK: "CK",
  COL: "CO",
  COM: "KM",
  CPV: "CV",
  CRI: "CR",
  CUB: "CU",
  CUW: "CW",
  CXR: "CX",
  CYM: "KY",
  CYP: "CY",
  CZE: "CZ",
  DEU: "DE",
  DJI: "DJ",
  DMA: "DM",
  DNK: "DK",
  DOM: "DO",
  DZA: "DZ",
  ECU: "EC",
  EGY: "EG",
  ERI: "ER",
  ESH: "EH",
  ESP: "ES",
  EST: "EE",
  ETH: "ET",
  FIN: "FI",
  FJI: "FJ",
  FLK: "FK",
  FRA: "FR",
  FRO: "FO",
  FSM: "FM",
  FXX: "FX",
  GAB: "GA",
  GBR: "GB",
  GEO: "GE",
  GGY: "GG",
  GHA: "GH",
  GIB: "GI",
  GIN: "GN",
  GLP: "GP",
  GMB: "GM",
  GNB: "GW",
  GNQ: "GQ",
  GRC: "GR",
  GRD: "GD",
  GRL: "GL",
  GTM: "GT",
  GUF: "GF",
  GUM: "GU",
  GUY: "GY",
  HKG: "HK",
  HMD: "HM",
  HND: "HN",
  HRV: "HR",
  HTI: "HT",
  HUN: "HU",
  IDN: "ID",
  IMN: "IM",
  IND: "IN",
  IOT: "IO",
  IRL: "IE",
  IRN: "IR",
  IRQ: "IQ",
  ISL: "IS",
  ISR: "IL",
  ITA: "IT",
  JAM: "JM",
  JEY: "JE",
  JOR: "JO",
  JPN: "JP",
  KAZ: "KZ",
  KEN: "KE",
  KGZ: "KG",
  KHM: "KH",
  KIR: "KI",
  KNA: "KN",
  KOR: "KR",
  KWT: "KW",
  LAO: "LA",
  LBN: "LB",
  LBR: "LR",
  LBY: "LY",
  LCA: "LC",
  LIE: "LI",
  LKA: "LK",
  LSO: "LS",
  LTU: "LT",
  LUX: "LU",
  LVA: "LV",
  MAC: "MO",
  MAF: "MF",
  MAR: "MA",
  MCO: "MC",
  MDA: "MD",
  MDG: "MG",
  MDV: "MV",
  MEX: "MX",
  MHL: "MH",
  MKD: "MK",
  MLI: "ML",
  MLT: "MT",
  MMR: "MM",
  MNE: "ME",
  MNG: "MN",
  MNP: "MP",
  MOZ: "MZ",
  MRT: "MR",
  MSR: "MS",
  MTQ: "MQ",
  MUS: "MU",
  MWI: "MW",
  MYS: "MY",
  MYT: "YT",
  NAM: "NA",
  NCL: "NC",
  NER: "NE",
  NFK: "NF",
  NGA: "NG",
  NIC: "NI",
  NIU: "NU",
  NLD: "NL",
  NOR: "NO",
  NPL: "NP",
  NRU: "NR",
  NZL: "NZ",
  OMN: "OM",
  PAK: "PK",
  PAN: "PA",
  PCN: "PN",
  PER: "PE",
  PHL: "PH",
  PLW: "PW",
  PNG: "PG",
  POL: "PL",
  PRI: "PR",
  PRK: "KP",
  PRT: "PT",
  PRY: "PY",
  PSE: "PS",
  PYF: "PF",
  QAT: "QA",
  REU: "RE",
  ROU: "RO",
  RUS: "RU",
  RWA: "RW",
  SAU: "SA",
  SDN: "SD",
  SEN: "SN",
  SGP: "SG",
  SGS: "GS",
  SHN: "SH",
  SJM: "SJ",
  SLB: "SB",
  SLE: "SL",
  SLV: "SV",
  SMR: "SM",
  SOM: "SO",
  SPM: "PM",
  SRB: "RS",
  SSD: "SS",
  STP: "ST",
  SUR: "SR",
  SVK: "SK",
  SVN: "SI",
  SWE: "SE",
  SWZ: "SZ",
  SXM: "SX",
  SYC: "SC",
  SYR: "SY",
  TCA: "TC",
  TCD: "TD",
  TGO: "TG",
  THA: "TH",
  TJK: "TJ",
  TKL: "TK",
  TKM: "TM",
  TLS: "TL",
  TON: "TO",
  TTO: "TT",
  TUN: "TN",
  TUR: "TR",
  TUV: "TV",
  TWN: "TW",
  TZA: "TZ",
  UGA: "UG",
  UKR: "UA",
  UMI: "UM",
  URY: "UY",
  USA: "US",
  UZB: "UZ",
  VAT: "VA",
  VCT: "VC",
  VEN: "VE",
  VGB: "VG",
  VIR: "VI",
  VNM: "VN",
  VUT: "VU",
  WLF: "WF",
  WSM: "WS",
  YEM: "YE",
  ZAF: "ZA",
  ZMB: "ZM",
  ZWE: "ZW",
};

const countryNamesByCodeVariable = `${variablePrefix}__countryNamesByCode`;

/**
 * Returns a table of country names in the user’s preferred language by ISO 3166-1 alpha-3 code.
 *
 * @param {string[]} locales - The locales for formatting the country names.
 * @param {object} options
 * @param {boolean} [options.uppercase] Whether to write the country names in all uppercase, respecting the locale’s case conventions.
 */
export function getLocalizedCountryNames(locales, options = {}) {
  let countryNames = new Intl.DisplayNames(locales, {
    type: "region",
    fallback: "none",
  });
  return Object.fromEntries(
    Object.entries(iso3166_1_alpha_2_by_3).map((e) => {
      let name = countryNames.of(e[1]);
      if (name && options?.uppercase) {
        // Neither the upcase expression operator nor the text-transform layout property is locale-aware, so uppercase the name upfront.
        name = name
          .toLocaleUpperCase(locales)
          // Word boundaries are less discernible in uppercase text, so pad each word by an additional space.
          .replaceAll(" ", "  ");
      }
      return [e[0], name];
    }),
  );
}

/**
 * Returns the global state that Diplomat needs to fully localize the style.
 *
 * @param {string[]} locales - The locales for formatting the country names.
 * @param {object} options
 * @param {boolean} [options.uppercaseCountryNames] Whether to write country names in all uppercase, respecting the locale’s case conventions.
 */
export function getGlobalStateForLocalization(locales, options = {}) {
  let state = {};
  state[countryNamesByCodeVariable] = getLocalizedCountryNames(locales, {
    uppercase: options?.uppercaseCountryNames,
  });
  return state;
}

/**
 * Returns an expression that converts the given country code to a human-readable name in the user's preferred language.
 *
 * @param {Expression} code An expression that evaluates to an ISO 3166-1 alpha-3 country code.
 * @returns {Expression}
 */
export function getLocalizedCountryNameExpression(code) {
  return [
    "let",
    "code",
    code,
    [
      "coalesce",
      [
        "get",
        ["var", "code"],
        [
          "coalesce",
          ["global-state", countryNamesByCodeVariable],
          ["literal", {}],
        ],
      ],
      // Fall back to the country code in parentheses.
      ["concat", "(", ["var", "code"], ")"],
    ],
  ];
}

/**
 * Updates each style layer's `text-field` value to match the given locales, upgrading any unlocalizable layer along the way.
 *
 * This method ugprades unlocalizable layers to localized multiline or inline labels depending on the `symbol-placement` layout property. To add a dual language label to a layer, set its `text-field` layout property manually using the `localizedNameWithLocalGloss` constant.
 *
 * If neither `options.layers` nor `options.sourceLayers` is specified, this function makes localizable any style layer that gets the feature property specified in `options.unlocalizedNameProperty`, or `name` by default.
 *
 * @param {maplibregl.Map} map - The map to localize.
 * @param {string[]} locales - The locales to insert into each layer.
 * @param {object} options
 * @param {string[]} [options.layers] - The style layers with these IDs will be made localizable.
 * @param {string[]} [options.sourceLayers] - The style layers that use these source layers will be made localizable. These are source layer IDs, not style layer IDs.
 * @param {string} [options.unlocalizedNameProperty] - The name of the property holding the unlocalized name.
 * @param {string} [options.localizedNamePropertyFormat] - The format of properties holding localized names, where `$1` is replaced by an IETF language tag.
 * @param {boolean} [options.glossLocalNames] - Whether to format each label as a dual language label including a local name gloss.
 * @param {boolean} [options.uppercaseCountryNames] Whether to write country names in all uppercase, respecting the locale’s case conventions.
 */
export function localizeStyle(map, locales = getLocales(), options = {}) {
  let style = map.getStyle();

  let localizedNameExpression = getLocalizedNameExpression(locales, options);
  let legacyLocalizedNameExpression = getLocalizedNameExpression(locales, {
    ...options,
    includesLegacyFields: true,
  });

  for (let layer of style.layers) {
    let sourceLayer = layer["source-layer"];
    if (
      (!options.layers && !options.sourceLayers) ||
      options.layers?.includes(layer.id) ||
      (sourceLayer && options.sourceLayers?.includes(sourceLayer))
    ) {
      prepareLayer(
        layer,
        options?.unlocalizedNameProperty,
        options?.glossLocalNames,
      );
    }
    localizeLayer(
      layer,
      locales[0],
      localizedNameExpression,
      legacyLocalizedNameExpression,
    );
  }

  let countryNames = getLocalizedCountryNames(locales, {
    uppercase: options?.uppercaseCountryNames,
  });
  map.setGlobalStateProperty(countryNamesByCodeVariable, countryNames);

  map.setStyle(style);
}

if (typeof window !== "undefined") {
  window.Diplomat = {
    getGlobalStateForLocalization,
    getLanguageFromURL,
    getLocales,
    getLocalizedCountryNameExpression,
    listValuesExpression,
    localizeLayers,
    localizeStyle,
    localizedName,
    localizedNameInline,
    localizedNameWithLocalGloss,
  };
  if ("maplibregl" in window) {
    maplibregl.Diplomat = window.Diplomat;

    maplibregl.Map.prototype.localizeStyle = function (
      locales = getLocales(),
      options = {},
    ) {
      localizeStyle(this, locales, options);
    };
  }
}
